import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  attachClosestEdge,
  extractClosestEdge,
  type Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { css } from '@emotion/react'
import { kebabCase } from 'change-case'
import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Color, ZIndex } from '@opallabs/gem'
import { useTheme, DragDirection } from '@opallabs/hooks'
import { WithStyleOverrides } from '../../gem'
import { Icon } from '../Icon'
import { Switch } from '../Switch'
import { TextInput } from '../TextInput'
import { Tooltip } from '../Tooltip'
import * as styles from './styles'

export type ToggleOption = {
  id: string

  /**
   * `disabled` controls whether the toggle can be changed.
   * The actual toggle status is controlled by the `value` prop.
   */
  disabled?: boolean
  /**
   * `nonReorderable` hides the drag handle for this item even when the
   * surrounding list has `reorderTogglesEnabled`. Use to lock a row in place
   * while keeping its toggle interactive (e.g. a row that's currently off
   * and shouldn't be drag-positioned until the user re-enables it).
   */
  nonReorderable?: boolean
  displayLabel?: string | JSX.Element
  displayName: string
  /**
   * @deprecated Use `leadingNode` instead.
   */
  leadingIcon?: {
    glyph: string
    color?: string
    size?: number
    useHypernova?: boolean
  }
  leadingNode?: React.ReactNode
  trailingNode?: React.ReactNode
  position?: number
  tooltipText?: React.ReactElement | string
  value: boolean
}

export type OnReorderCallback<T extends ToggleOption> = (args: {
  dragDirection: DragDirection
  /** The toggle that was dragged */
  draggedFromToggle: T
  /** The toggle `draggedFromToggle` was dropped before or after */
  draggedToToggle: T
}) => void

export type OnToggleCallback<T extends ToggleOption> = (
  updatedToggle: T
) => void

export type ToggleListProps<T extends ToggleOption> = WithStyleOverrides & {
  hideInactiveToggles?: boolean
  onReorder?: OnReorderCallback<T>
  onToggleChange: OnToggleCallback<T>
  reorderTogglesEnabled?: boolean
  searchable?: boolean
  toggles: T[]
}

/**
 * Rearrangable, toggleable list of items
 *
 * `hideInactiveToggles` hides elements in the `toggles` array that have
 * `value=false`.
 *
 * @note This component doesn’t currently have defined behavior for reordering
 * items when there are hidden items and/or an active search. (The code doesn’t
 * explicitly handle it, and there are no tests for it.) `@opallabs/satellite`’s
 * `<CustomFieldsSelectList>` wraps `onReorder` to produce a well-defined
 * result when inactive items are hidden, and that logic could be moved to this
 * component.
 */
export const ToggleList = <T extends ToggleOption = ToggleOption>({
  hideInactiveToggles = false,
  onReorder,
  onToggleChange,
  reorderTogglesEnabled = false,
  searchable = false,
  styleOverrides,
  toggles,
}: ToggleListProps<T>) => {
  const theme = useTheme()
  const [searchValue, setSearchValue] = useState('')
  const [focusedToggleId, setFocusedToggleId] = useState<string | null>(null)

  const ListTag: keyof JSX.IntrinsicElements = reorderTogglesEnabled
    ? 'ol'
    : 'ul'

  const filteredList = useMemo(() => {
    if (!searchable || !searchValue) return toggles
    return toggles.filter((toggle) =>
      toggle.displayName.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [searchable, searchValue, toggles])

  useEffect(() => {
    const cleanup = monitorForElements({
      canMonitor: () => reorderTogglesEnabled,
      onDrop: ({ source, location }) => {
        // Our types currently don't force the presence of `onReorder` when
        // `reorderTogglesEnabled=true`, so this is a realistic scenario.
        if (!onReorder) return

        const target = location.current.dropTargets[0]
        // Happens when dropping the `<ToggleListItem>` outside `<ToggleList>`,
        // or when dropping an item on itself.
        if (!target) return

        if (
          !isToggleListDraggable(target.data) ||
          !isToggleListDraggable(source.data)
        ) {
          return
        }

        const dragDirection =
          source.data.index < target.data.index ? 'increase' : 'decrease'

        const fromToggle = toggles.find(
          (toggle) => toggle.id === source.data.id
        )
        const toToggle = toggles.find((toggle) => toggle.id === target.data.id)

        if (fromToggle !== undefined && toToggle !== undefined) {
          onReorder({
            dragDirection,
            draggedFromToggle: fromToggle,
            draggedToToggle: toToggle,
          })
        }
      },
    })
    return cleanup
  }, [onReorder, reorderTogglesEnabled, toggles])

  return (
    <div className="lapidary-toggle-list-container">
      {searchable && (
        <TextInput
          trailingIconGlyph="gem-search"
          placeholder="Search..."
          onChange={(value) => setSearchValue(value)}
          initialValue={searchValue}
          styleOverrides={{
            container: styles.searchOverrides(theme),
          }}
        />
      )}
      <ListTag
        css={[styles.listContainer(searchable), styleOverrides]}
        className="lapidary-toggle-list"
      >
        {filteredList
          .filter((toggle) => (hideInactiveToggles ? toggle.value : true))
          .map((toggle, index) => {
            return (
              <ToggleListItem<T>
                key={toggle.id}
                focusedToggleId={focusedToggleId}
                listIndex={index}
                onChange={onToggleChange}
                reorderTogglesEnabled={reorderTogglesEnabled}
                searchValue={searchValue}
                setFocusedToggleId={setFocusedToggleId}
                {...toggle}
              />
            )
          })}
      </ListTag>
    </div>
  )
}
ToggleList.displayName = 'Lapidary: ToggleList'

type ToggleListItemProps<T extends ToggleOption> = T & {
  focusedToggleId: string | null
  listIndex: number
  onChange: OnToggleCallback<T>
  reorderTogglesEnabled: ToggleListProps<T>['reorderTogglesEnabled']
  searchValue: string
  setFocusedToggleId: (id: string | null) => void
}

const ToggleListItem = <T extends ToggleOption>({
  disabled,
  displayLabel,
  displayName,
  focusedToggleId,
  id,
  leadingIcon,
  leadingNode,
  trailingNode,
  listIndex,
  nonReorderable,
  onChange,
  position,
  reorderTogglesEnabled = false,
  searchValue,
  setFocusedToggleId,
  tooltipText,
  value,
}: ToggleListItemProps<T>) => {
  const theme = useTheme()
  const ref = useRef<HTMLLIElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  // Reorderable toggles should have a position attribute, but if not
  // we'll fall back to the toggle's array index
  const togglePosition = useMemo(
    () => position ?? listIndex,
    [listIndex, position]
  )

  const dragData = useMemo(
    () => ({
      type: 'toggle-list-item',
      id,
      index: togglePosition,
    }),
    [id, togglePosition]
  )

  useEffect(() => {
    const element = ref.current

    if (!element) {
      throw new Error('ToggleListItem: Invalid ref.')
    }

    const cleanup = combine(
      draggable({
        element,
        getInitialData: () => dragData,
        canDrag: () => {
          return (
            reorderTogglesEnabled &&
            !nonReorderable &&
            focusedToggleId !== null &&
            focusedToggleId === dragData.id
          )
        },
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          return (
            isToggleListDraggable(source.data) && source.data.id !== dragData.id
          )
        },
        getData: ({ input }) => {
          return attachClosestEdge(dragData, {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          })
        },
        onDrag: ({ self }) => {
          const newClosestEdge = extractClosestEdge(self.data)
          setClosestEdge(newClosestEdge)
        },
        onDragLeave() {
          setClosestEdge(null)
        },
        onDrop() {
          setClosestEdge(null)
        },
      })
    )

    return cleanup
  }, [dragData, focusedToggleId, id, nonReorderable, reorderTogglesEnabled])

  return (
    <li
      className={classNames('toggle-list-item', { dragging })}
      css={styles.listItem(theme)}
      key={id}
      ref={ref}
    >
      <DropIndicator edge="top" visible={closestEdge === 'top'} />
      {reorderTogglesEnabled && !nonReorderable && !searchValue && (
        <button
          css={styles.dragHandle({ dragging, theme })}
          type="button"
          tabIndex={-1}
          onMouseDown={() => setFocusedToggleId(id)}
          onMouseUp={() => setFocusedToggleId(null)}
        >
          ⠿
        </button>
      )}
      <label
        css={styles.label(theme, dragging)}
        htmlFor={`lapidary-toggle-${kebabCase(displayName)}`}
      >
        {leadingIcon && <Icon {...leadingIcon} ariaHidden />}
        {leadingNode}
        <Tooltip
          disabled={!tooltipText}
          content={tooltipText ?? ''}
          side="top"
          zIndex={ZIndex.Drawer.Tooltip}
        >
          <span css={styles.lineClamp(displayLabel as string)}>
            {displayName}
          </span>
        </Tooltip>
        {trailingNode}
      </label>
      {displayLabel ? (
        <label css={styles.displayLabel(theme, dragging)}>{displayLabel}</label>
      ) : null}
      {!dragging && (
        <Switch
          checked={value}
          id={`lapidary-toggle-${kebabCase(displayName)}`}
          onToggle={(value: boolean) =>
            // This type assertion is safe--the arguments are all from the `T`
            // passed to the component.
            //
            // Fixes `'{ ... }' is assignable to the constraint of type 'T', but
            // 'T' could be instantiated with a different subtype of constraint
            // 'ToggleOption'. ts(2345)`
            onChange({
              disabled,
              displayLabel,
              displayName,
              id,
              leadingNode,
              trailingNode,
              leadingIcon,
              position,
              tooltipText,
              value,
            } as T)
          }
          readonly={disabled}
        />
      )}
      <DropIndicator edge="bottom" visible={closestEdge === 'bottom'} />
    </li>
  )
}

ToggleListItem.displayName = 'Lapidary: ToggleListItem ← ToggleList'

type ToggleListDraggable = {
  id: string
  index: number
  type: 'toggle-list-item'
}

const isToggleListDraggable = (
  draggable: Record<string, unknown>
): draggable is ToggleListDraggable => {
  const properties = ['type', 'id', 'index'] as const
  return (
    properties.every((prop) => prop in draggable) &&
    draggable.type === 'toggle-list-item'
  )
}

const DropIndicator = ({
  edge,
  visible,
}: {
  edge: 'top' | 'bottom'
  visible: boolean
}) => {
  const theme = useTheme()

  const styles = useMemo(() => {
    // When applied together on the top/bottom edges, this creates a
    // 2px tall box centered on the edge of the parent element
    const outsideBoundary = '-1px'
    const insideBoundary = 'calc(100% - 1px)'

    return css({
      position: 'absolute',
      left: 0,
      right: 0,
      top: edge === 'top' ? outsideBoundary : insideBoundary,
      bottom: edge === 'bottom' ? outsideBoundary : insideBoundary,
      height: 2,
      backgroundColor: visible ? Color[theme].Surface.A200 : 'transparent',
    })
  }, [edge, theme, visible])

  return <div css={styles} />
}
DropIndicator.displayName = 'Lapidary: DropIndicator ← ToggleList'
